import Fuse from 'fuse.js'
import groupBy from 'lodash/groupBy'
import orderBy from 'lodash/orderBy'
import uniqBy from 'lodash/uniqBy'
import memoize from 'moize'
import ms from 'ms'

import arriereBackoffice, {
  BookingStatus,
  HomeSorting,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { SortOrderKey } from '@new/__global/components/FiltersFramework'
import { noEciLco, VALID_BOOKING_STATUSES } from '@new/__global/constants/calendar'

export const currencyFormatter = new Intl.NumberFormat('en-us', {
  currency: 'USD',
  style: 'currency',
  maximumFractionDigits: 0,
})

export const arriere = {
  fetchBookings: memoize(
    async ({
      startDate,
      endDate,
      homeIds,
      filters,
    }: {
      startDate: string
      endDate: string
      homeIds: string[]
      filters?: Record<string, any>
    }) => {
      const { results: bookings } = await arriereBackoffice.queries.bookings.memo({
        __args: {
          from: startDate,
          to: endDate,
          status: VALID_BOOKING_STATUSES,
          homeIds,
          pagination: { page: 1, size: 99999 },
          ...filters,
        },
        results: {
          id: true,
          source: true,
          homeId: true,
          guestName: true,
          notes: true,
          status: true,
          activeServicesCount: true,
          issues: true,
          securityDepositStatus: true,
          isRoomAssigned: true,
          folio: {
            summary: {
              paymentBalanceStatus: true,
            },
          },
          checkInTiming: {
            originalTime: true,
            effectiveTime: true,
          },
          checkOutTiming: {
            originalTime: true,
            effectiveTime: true,
          },
        },
      })

      return orderBy(bookings, it => it.checkInTiming.effectiveTime)
    },
    {
      maxAge: ms('5 minutes'),
    },
  ),
  fetchUnassignedBookings: memoize(
    async ({
      startDate,
      endDate,
      homeIds,
      filters,
    }: {
      startDate: string
      endDate: string
      homeIds: string[]
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filters?: Record<string, any>
    }) => {
      const { results: bookings } = await arriereBackoffice.queries.bookings.memo({
        __alias: 'tapeChartUnassignedBookings',
        __args: {
          from: startDate,
          to: endDate,
          status: [
            BookingStatus.booked,
            BookingStatus.checkedIn,
            BookingStatus.requested,
            BookingStatus.modified,
          ],
          homeIds,
          pagination: { page: 1, size: 99999 },
          assignedRoom: false,
          ...filters,
        },
        results: {
          id: true,
          source: true,
          homeId: true,
          guestName: true,
          notes: true,
          status: true,
          activeServicesCount: true,
          issues: true,
          securityDepositStatus: true,
          hotelRoomType: {
            id: true,
            roomKind: true,
          },
          folio: {
            summary: {
              paymentBalanceStatus: true,
            },
          },
          checkInTiming: {
            originalTime: true,
            effectiveTime: true,
          },
          checkOutTiming: {
            originalTime: true,
            effectiveTime: true,
          },
        },
      })

      return orderBy(bookings, it => it.checkInTiming.effectiveTime)
    },
    {
      maxAge: ms('5 minutes'),
    },
  ),

  fetchTapeChartData: memoize(
    async ({
      startDate,
      endDate,
      homeIds,
    }: {
      startDate: string
      endDate: string
      homeIds: string[]
    }) => {
      const { searchAvailabilityBlocks, searchHomeDatedPrices } =
        await arriereBackoffice.queries.tapeChart({
        __alias: 'tapeChartBlocksAndRates',
        __args: {
          period: {
            start: startDate,
            end: endDate,
          },
          homeIds,
        },
        searchAvailabilityBlocks: {
          id: true,
          homeId: true,
          period: { start: true, end: true },
          source: true,
          createdAt: true,
          createdBy: true,
          updatedAt: true,
          updatedBy: true,
          note: true,
          notificationSetting: {
            notifyAuthor: true,
            notifyRelocationTeam: true,
            notifyFieldOpsTeam: true,
          },
          parent: {
            bookingId: true,
            id: true,
            homeId: true,
            period: {
              start: true,
              end: true,
            },
            source: true,
          },
          expiresAt: true,
          booking: {
            detailedCheckIn: {
              effectiveTime: true,
            },
            detailedCheckOut: {
              effectiveTime: true,
            },
            hash: true,
            guestName: true,
            home: {
              name: true,
            },
          },
        },
        searchHomeDatedPrices: {
          date: true,
          homeId: true,
          minStay: true,
          maxStay: true,
          rate: true,
        },
      })

      const blocks = uniqBy(
        searchAvailabilityBlocks,
        it => `${it.homeId},${it.period.start},${it.period.end},${it.source},${it.note}`,
      ).filter(noEciLco)

      const rates = groupBy(searchHomeDatedPrices, it => it.homeId)

      return { blocks, rates }
    },
    {
      maxAge: ms('5 minutes'),
    },
  ),

  fetchListings: async ({
    terms,
    page = 1,
    pageSize = 100,
    filters,
    sortBy,
    sortOrder,
  }: {
    terms: string
    page?: number
    pageSize?: number
    filters: Record<string, any>
    sortBy?: string
    sortOrder?: SortOrderKey
  }) => {
    const homesList = await arriere.fetchAllListings(filters)

    const mappedHomeList = homesList?.map(it => ({
      ...it,
      terms: [
        it.region?.name,
        it.name,
        ...(it.region?.name || '').split(/\s+/),
        ...it.name.split(/\s+-\s+/),
        ...it.name.split(/[\s-]+/),
      ],
    }))

    const { searchableList, rawList } = {
      rawList: mappedHomeList,
      searchableList: new Fuse(mappedHomeList, {
        keys: ['terms'],
        shouldSort: false,
        threshold: 0.25,
        location: 0,
        distance: 3,
      }),
    }

    const results = !terms
      ? rawList
      : searchableList
          .search({
            $or: [
              { terms: terms.trim() },
              {
                $and: terms
                  .trim()
                  .split(/\W+/)
                  .map(it_1 => ({ terms: it_1 })),
              },
            ],
          })
          .map(it_2 => it_2.item)

    const offset = (page - 1) * pageSize

    const sortedResults =
      sortBy && sortOrder ? orderBy(results, [`home.${sortBy}`], [sortOrder]) : results

    const currentPageOfResults = sortedResults.slice(offset, offset + pageSize)

    return {
      total: results.length,
      results: currentPageOfResults,
      sortedRawResults: sortedResults,
      rawResults: rawList,
    }
  },

  fetchAllListings: memoize(
    async filters => {
      const { results: homes } = await arriereBackoffice.queries.tapeChartPaginatedHomes({
        __alias: 'fetchAllListings',
        __args: {
          ...filters,
          pagination: {
            page: 1,
            size: 9999,
          },
          sortBy: HomeSorting.regionName,
        },
        results: {
          id: true,
          name: true,
          region: {
            id: true,
            name: true,
          },
          home: {
            latitude: true,
            longitude: true,
            revenueModel: true,
            maxOccupancy: true,
            bedroomsCount: true,
            bathroomsCount: true,
          },
          guestReadinessStatus: true,
          lifecycle: {
            riskLevel: true,
          },
        },
      })

      return homes.map(home => {
        const tapeContextBasicInfo = {
          group: {
            name: home.region?.name,
            id: home.region?.id,
            action: undefined,
          },
          itemKey: home.id,
        }

        return {
          ...home,
          ...tapeContextBasicInfo,
        }
      })
    },
    { maxAge: 3600000 },
  ),

  fetchHomesListExtendedInfo: memoize(
    async ({ homeIds }: { homeIds: string[] }) => {
      const { results: homes } = await arriereBackoffice.queries.tapeChartPaginatedHomes({
        __alias: 'fetchAllHomesExtendedInfo',
        __args: {
          homeIds,
          pagination: {
            page: 1,
            size: 9999,
          },
        },
        results: {
          id: true,
          home: {
            operationReadinessStatus: {
              status: true,
              othersCases: true,
              othersCasesDetails: {
                cases: {
                  isHighOrUrgentPriority: true,
                  workOrderId: true,
                },
                completed: {
                  workOrderId: true,
                },
                status: true,
                total: true,
                referencePeriod: {
                  end: true,
                  start: true,
                },
              },
              cleanDetails: {
                status: true,
                cases: {
                  workOrderId: true,
                },
                total: true,
                referencePeriod: {
                  end: true,
                  start: true,
                },
              },
              inspectedDetails: {
                status: true,
                cases: {
                  workOrderId: true,
                },
                total: true,
              },
            },
            operationTags: {
              currentBookingId: true,
              short: true,
            },
          },
        },
      })

      return homes
    },
    {
      maxAge: ms('60 seconds'),
    },
  ),

  fetchExtendedColumns: memoize(
    async columns => {
      return columns
    },
    { maxAge: 3600000 },
  ),
}
